import classNames from "classnames";
import { useState } from "react";
import { DashboardUtils } from "../../../dashboard_utils";
import { trpc } from "../../../utils/trpc";
import { SeshPrimaryButton } from "../base_components/sesh_primary_button";
import { LoadingThrobber } from "../base_components/loading_throbber";
import { PaymentInterval } from "../../../../../../common/premium_tiers";

export interface SubscriptionUpgradeModalProps {
	open: boolean;
	onClose: () => void;
	entitlementId: string;
	proCount: number;
	isBundle: boolean;
	onSuccess: () => void;
}

/**
 * Format cents to a currency string using Intl.NumberFormat
 */
function formatCurrency(cents: number, currency: string = "usd"): string {
	return new Intl.NumberFormat("en-US", {
		style: "currency",
		currency: currency.toUpperCase(),
	}).format(cents / 100);
}

/**
 * Format payment interval to a readable string
 */
function formatInterval(interval: PaymentInterval): string {
	switch (interval) {
		case PaymentInterval.Month:
			return "Monthly";
		case PaymentInterval.Year:
			return "Yearly";
		default:
			return "";
	}
}

export function SubscriptionUpgradeModal(props: SubscriptionUpgradeModalProps) {
	const { open, onClose, entitlementId, proCount, isBundle, onSuccess } =
		props;
	const [isUpgrading, setIsUpgrading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	// Fetch upgrade preview
	const previewQuery = trpc.premium.getSubscriptionUpgradePreview.useQuery(
		{
			entitlement_id: entitlementId,
			pro_count: proCount,
		},
		{
			enabled: open,
		}
	);

	// Execute upgrade mutation
	const upgradeMutation = trpc.premium.executeSubscriptionUpgrade.useMutation(
		{
			onSuccess: () => {
				setIsUpgrading(false);
				onSuccess();
			},
			onError: (err) => {
				setIsUpgrading(false);
				setError(err.message || "Failed to process upgrade");
			},
		}
	);

	if (!open) {
		return null;
	}

	const handleConfirmUpgrade = () => {
		setIsUpgrading(true);
		setError(null);
		upgradeMutation.mutate({
			entitlement_id: entitlementId,
			pro_count: proCount,
		});
	};

	const preview = previewQuery.data;
	const isLoading = previewQuery.isLoading;
	const previewError = previewQuery.error;

	return (
		<DashboardUtils.SeshModal
			is_active={open}
			onClose={onClose}
			title="Confirm Upgrade"
			subtitle={
				isBundle
					? `Upgrade ${proCount} slot${proCount > 1 ? "s" : ""} to sesh Pro`
					: "Upgrade to sesh Pro"
			}
			content={
				<div className="mt-4 flex flex-col gap-6">
					{isLoading && (
						<LoadingThrobber
							loading_text="Calculating upgrade price..."
							vertical_spaces={2}
						/>
					)}

					{previewError && (
						<div className="rounded-lg border border-red-500/50 bg-red-500/10 p-4">
							<div className="flex items-center gap-2 text-red-400">
								<i className="fas fa-exclamation-circle"></i>
								<span>
									{previewError.message ||
										"Failed to load upgrade preview"}
								</span>
							</div>
						</div>
					)}

					{preview && (
						<>
							{/* License breakdown - before and after */}
							<div className="rounded-lg border border-slate-600 bg-slate-800/50 p-4">
								<h4 className="mb-4 text-sm font-medium text-slate-200">
									Subscription Changes
								</h4>
								<div className="flex flex-col gap-3">
									{/* Before → After comparison */}
									<div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3 text-sm">
										<div className="rounded bg-slate-700/50 p-3">
											<div className="mb-1 text-xs font-medium text-slate-400">
												Current
											</div>
											{preview.current_premium_count > 0 && (
												<div className="text-slate-300">
													{preview.current_premium_count} Premium license
													{preview.current_premium_count > 1 ? "s" : ""}
												</div>
											)}
											{preview.current_pro_count > 0 && (
												<div className="text-amber-400">
													{preview.current_pro_count} Pro license
													{preview.current_pro_count > 1 ? "s" : ""}
												</div>
											)}
										</div>
										<div className="text-slate-500">
											<i className="fas fa-arrow-right"></i>
										</div>
										<div className="rounded bg-slate-700/50 p-3">
											<div className="mb-1 text-xs font-medium text-slate-400">
												After Upgrade
											</div>
											{preview.resulting_premium_count > 0 && (
												<div className="text-slate-300">
													{preview.resulting_premium_count} Premium license
													{preview.resulting_premium_count > 1 ? "s" : ""}
												</div>
											)}
											{preview.resulting_pro_count > 0 && (
												<div className="text-amber-400">
													{preview.resulting_pro_count} Pro license
													{preview.resulting_pro_count > 1 ? "s" : ""}
												</div>
											)}
										</div>
									</div>

									{/* Explanation */}
									<p className="text-xs text-slate-400">
										{isBundle
											? `Converting ${proCount} of your Premium license${proCount > 1 ? "s" : ""} to Pro. Pro includes all Premium features plus API access, custom branding, and more.`
											: "Your Premium license will be converted to Pro, which includes all Premium features plus API access, custom branding, and more."}
									</p>
								</div>
							</div>

							{/* Amount due today (prorated) */}
							<div className="rounded-lg border border-slate-600 bg-slate-800/50 p-4">
								<div className="mb-4 flex items-center justify-between">
									<h4 className="text-sm font-medium text-slate-200">
										{preview.is_trialing
											? "Amount Due When Trial Ends"
											: "Prorated Amount Due Today"}
									</h4>
									<span className="rounded bg-amber-600/80 px-2 py-1 text-xs font-bold text-white">
										{formatInterval(preview.payment_interval)} Billing
									</span>
								</div>
								<div className="flex flex-col gap-2">
									{/* Stripe's prorated line items */}
									{preview.line_items?.map((item, index) => (
										<div
											key={index}
											className="flex items-start justify-between gap-4 text-sm"
										>
											<span
												className={classNames(
													"flex-1",
													item.amount < 0
														? "text-green-400"
														: "text-slate-300"
												)}
											>
												{item.description}
												{item.proration && (
													<span className="ml-1 text-xs text-slate-500">
														(prorated)
													</span>
												)}
											</span>
											<span
												className={classNames(
													"whitespace-nowrap font-medium",
													item.amount < 0
														? "text-green-400"
														: "text-slate-200"
												)}
											>
												{item.amount < 0 ? "-" : ""}
												{formatCurrency(
													Math.abs(item.amount),
													preview.currency
												)}
											</span>
										</div>
									))}

									{/* Divider */}
									<div className="my-2 border-t border-slate-600"></div>

									{/* Total due today */}
									<div className="flex items-center justify-between">
										<span className="font-medium text-slate-200">Total</span>
										<span className="text-lg font-bold text-sesh-primary">
											{formatCurrency(
												preview.amount_due,
												preview.currency
											)}
										</span>
									</div>
									<p className="text-xs text-slate-400">
										{preview.is_trialing ? (
											<>
												Your trial ends on{" "}
												{new Date(
													(preview.trial_end ?? preview.current_period_end) * 1000
												).toLocaleDateString()}
												—you won't be charged until then.
											</>
										) : (
											<>
												Prorated for the remainder of your{" "}
												{preview.payment_interval === PaymentInterval.Month
													? "monthly"
													: "yearly"}{" "}
												billing period (until{" "}
												{new Date(
													preview.current_period_end * 1000
												).toLocaleDateString()}
												).
											</>
										)}
									</p>
								</div>
							</div>

							{/* Future recurring charge */}
							<div className="rounded-lg border border-slate-600 bg-slate-800/50 p-4">
								<h4 className="mb-3 text-sm font-medium text-slate-200">
									Future{" "}
									{preview.payment_interval === PaymentInterval.Month
										? "Monthly"
										: "Yearly"}{" "}
									Charge
								</h4>
								<div className="flex flex-col gap-2">
									<p className="text-xs text-slate-400">
										Starting{" "}
										{new Date(
											preview.current_period_end * 1000
										).toLocaleDateString()}
									</p>
									{preview.global_coupon?.is_recurring && preview.global_coupon.future_discount_amount > 0 ? (
										<>
											{/* Show breakdown when coupon is applied */}
											<div className="flex items-center justify-between text-sm">
												<span className="text-slate-300">
													Subscription
												</span>
												<span className="text-slate-300">
													{formatCurrency(
														preview.future_recurring_amount + preview.global_coupon.future_discount_amount,
														preview.currency
													)}
													<span className="ml-1 text-xs text-slate-400">
														/{preview.payment_interval === PaymentInterval.Month ? "mo" : "yr"}
													</span>
												</span>
											</div>
											<div className="flex items-center justify-between text-sm">
												<span className="text-green-400">
													<i className="fas fa-tag mr-1"></i>
													{preview.global_coupon.name}
												</span>
												<span className="text-green-400">
													-{formatCurrency(
														preview.global_coupon.future_discount_amount,
														preview.currency
													)}
													<span className="ml-1 text-xs text-green-400/70">
														/{preview.payment_interval === PaymentInterval.Month ? "mo" : "yr"}
													</span>
												</span>
											</div>
											{/* Divider */}
											<div className="my-1 border-t border-slate-600"></div>
											{/* Total */}
											<div className="flex items-center justify-between">
												<span className="font-medium text-slate-200">Total</span>
												<span className="text-lg font-bold text-slate-200">
													{formatCurrency(
														preview.future_recurring_amount,
														preview.currency
													)}
													<span className="ml-1 text-sm font-normal text-slate-400">
														/{preview.payment_interval === PaymentInterval.Month ? "mo" : "yr"}
													</span>
												</span>
											</div>
										</>
									) : (
										/* No coupon - show simple total */
										<div className="flex items-center justify-between">
											<span className="font-medium text-slate-200">Total</span>
											<span className="text-lg font-bold text-slate-200">
												{formatCurrency(
													preview.future_recurring_amount,
													preview.currency
												)}
												<span className="ml-1 text-sm font-normal text-slate-400">
													/{preview.payment_interval === PaymentInterval.Month ? "mo" : "yr"}
												</span>
											</span>
										</div>
									)}
								</div>
							</div>

							{/* Error display */}
							{error && (
								<div className="rounded-lg border border-red-500/50 bg-red-500/10 p-4">
									<div className="flex items-center gap-2 text-red-400">
										<i className="fas fa-exclamation-circle"></i>
										<span>{error}</span>
									</div>
								</div>
							)}

							{/* Confirm button */}
							<SeshPrimaryButton
								onClick={handleConfirmUpgrade}
								disabled={isUpgrading}
								is_loading={isUpgrading}
								className="w-full justify-center bg-sesh-primary font-semibold !text-base-dark hover:bg-cyan-300"
							>
								<i className="fas fa-crown mr-2 text-amber-300"></i>
								{preview.is_trialing ? (
									"Confirm Upgrade"
								) : (
									<>
										Confirm Upgrade -{" "}
										{formatCurrency(
											preview.amount_due,
											preview.currency
										)}
									</>
								)}
							</SeshPrimaryButton>

							<p className="text-center text-xs text-slate-400">
								{preview.is_trialing
									? "Your subscription will be updated immediately. You'll be charged when your trial ends."
									: "Your card on file will be charged. You can manage your subscription in the Stripe billing portal."}
							</p>
						</>
					)}
				</div>
			}
		/>
	);
}
